import * as go from 'gojs';

import {
  BaseLinkData,
  Mapping,
} from 'src/containers/ProjectDiagram/types/data';
import { MappingLink } from 'src/containers/ProjectDiagram/types/diagram';
import {
  isMapping,
  isModelConceptClass,
  isModelConceptRelationship,
  isRelationshipMapping,
  isResource,
} from 'src/containers/ProjectDiagram/utils/data';
import { getModelConceptClassKey } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { getResourceKey } from 'src/containers/ProjectDiagram/utils/resource/createResource';
import { EntityDrawerType } from 'src/store/ui/types';

export const getMappingIdForSelection = ({
  selection,
  entityDrawerType,
  entityDrawerValue,
  allMappings,
  linkDataArray,
}: {
  selection: go.Set<go.Part>;
  entityDrawerType: EntityDrawerType;
  entityDrawerValue: string | undefined;
  allMappings: Mapping[];
  linkDataArray: BaseLinkData[];
}): { mappingId: string | null; mappingLink?: MappingLink } => {
  const selectionArray = selection.toArray();

  // if a mapping link is selected, use it directly
  const mappingLink = selectionArray.find((part): part is MappingLink =>
    isMapping(part.data)
  );
  if (mappingLink) {
    return { mappingId: mappingLink.data.value, mappingLink };
  }

  // resolve entity from diagram selection, falling back to entity drawer
  const selectedData = selectionArray[0]?.data;
  if (selectedData && isResource(selectedData)) {
    return {
      mappingId:
        getMappingIdForEntity(
          allMappings,
          linkDataArray,
          EntityDrawerType.RESOURCE,
          selectedData.value
        ) ?? null,
    };
  }
  if (selectedData && isModelConceptClass(selectedData)) {
    return {
      mappingId:
        getMappingIdForEntity(
          allMappings,
          linkDataArray,
          EntityDrawerType.CLASS,
          selectedData.value
        ) ?? null,
    };
  }
  if (selectedData && isModelConceptRelationship(selectedData)) {
    return {
      mappingId:
        getMappingIdForEntity(
          allMappings,
          linkDataArray,
          EntityDrawerType.RELATIONSHIP,
          selectedData.value
        ) ?? null,
    };
  }

  return {
    mappingId:
      getMappingIdForEntity(
        allMappings,
        linkDataArray,
        entityDrawerType,
        entityDrawerValue
      ) ?? null,
  };
};

export const getMappingIdForEntity = (
  allMappings: Mapping[],
  linkDataArray: BaseLinkData[],
  entityType: EntityDrawerType,
  entityValue: string | undefined
): string | undefined => {
  if (
    !entityValue ||
    entityType === EntityDrawerType.NONE ||
    entityType === EntityDrawerType.SENSITIVE_PROPERTY_GROUP
  ) {
    return allMappings[0]?.value;
  }

  switch (entityType) {
    case EntityDrawerType.RESOURCE: {
      const resourceKey = getResourceKey(entityValue);
      const match = allMappings.find((m) => m.from === resourceKey);
      return match?.value ?? allMappings[0]?.value;
    }
    case EntityDrawerType.CLASS: {
      const classKey = getModelConceptClassKey(entityValue);
      const match = allMappings.find((m) => m.to === classKey);
      return match?.value ?? allMappings[0]?.value;
    }
    case EntityDrawerType.RELATIONSHIP: {
      const relMappingKeys = new Set(
        linkDataArray
          .filter(
            (link) =>
              isRelationshipMapping(link) &&
              link.relationshipIri === entityValue
          )
          .flatMap((link) => [link.from, link.to].filter(Boolean) as string[])
      );
      const match = allMappings.find((m) => relMappingKeys.has(m.key));
      return match?.value ?? allMappings[0]?.value;
    }
    case EntityDrawerType.ATTRIBUTE: {
      const match = allMappings.find((m) =>
        Object.values(m.fields).some((field) =>
          field.targets.includes(entityValue)
        )
      );
      return match?.value ?? allMappings[0]?.value;
    }
    default:
      return allMappings[0]?.value;
  }
};
